import React, { useState, useEffect, useRef, useCallback } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import {
  FeatherX,
  FeatherAlertCircle,
  FeatherUpload,
  FeatherFile,
  FeatherTrash2,
  FeatherSend,
  FeatherChevronDown,
  FeatherDownload,
  FeatherMessageCircle,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { DropdownMenu } from "../../../../ui/components/DropdownMenu";
import {
  TenantComplianceEntity,
  ComplianceProofEntity,
} from "../../domain/entities/TenantComplianceEntity";
import { TenantComplianceRepositoryImpl } from "../../data/repositories";
import {
  GetComplianceUseCase,
  UploadProofUseCase,
} from "../../domain/use-cases/dashboard";
import { useToast } from "../../../../shared/presentation/components";

interface ComplianceDetailPanelProps {
  complianceId: number | null;
  onClose: () => void;
  onRefresh?: () => void;
  onStatusUpdate?: (complianceId: number, newStatus: string) => void;
}

const complianceRepository = new TenantComplianceRepositoryImpl();
const getComplianceUseCase = new GetComplianceUseCase(complianceRepository);
const uploadProofUseCase = new UploadProofUseCase(complianceRepository);

export const ComplianceDetailPanel: React.FC<ComplianceDetailPanelProps> = ({
  complianceId,
  onClose,
  onRefresh,
  onStatusUpdate,
}) => {
  const { showSuccess, showError } = useToast();
  const [compliance, setCompliance] = useState<TenantComplianceEntity | null>(
    null
  );
  const [proofs, setProofs] = useState<ComplianceProofEntity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [proofType, setProofType] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragCounter = useRef<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Upload queue system
  interface PendingUpload {
    id: string; // temporary ID
    file: File;
    proofType: string;
    status: "pending" | "uploading" | "completed" | "error";
    error?: string;
    proofId?: number; // real ID after upload
    signed_url?: string; // signed URL from upload response
  }
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const uploadQueueRef = useRef<boolean>(false);
  const queuedFilesRef = useRef<Set<string>>(new Set());
  const processingFileRef = useRef<string | null>(null);

  const proofTypeOptions = ["challan", "payment_proof", "screenshot"];

  const fetchComplianceDetails = useCallback(async () => {
    if (!complianceId) return;

    try {
      setLoading(true);
      const result = await getComplianceUseCase.execute(complianceId);
      setCompliance(result);
      setProofs(result.proofs || []);
    } catch (err) {
      showError(
        err instanceof Error
          ? err.message
          : "Failed to fetch compliance details"
      );
    } finally {
      setLoading(false);
    }
  }, [complianceId, showError]);

  useEffect(() => {
    if (complianceId) {
      fetchComplianceDetails();
    } else {
      setCompliance(null);
      setProofs([]);
    }
  }, [complianceId, fetchComplianceDetails]);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  // Process upload queue with while loop
  const processUploadQueue = useCallback(async () => {
    console.log("[processUploadQueue] Called", {
      complianceId,
      uploadQueueRef: uploadQueueRef.current,
    });

    if (!complianceId || uploadQueueRef.current) {
      console.log("[processUploadQueue] Blocked", {
        hasComplianceId: !!complianceId,
        uploadQueueRef: uploadQueueRef.current,
      });
      return;
    }

    // Set ref to prevent concurrent processing
    uploadQueueRef.current = true;
    console.log("[processUploadQueue] Starting queue processing...");

    // While loop: continue as long as queue has pending items
    while (true) {
      let pending: PendingUpload | null = null;
      let queueLength = 0;

      // Read current state synchronously
      await new Promise<void>((resolve) => {
        setPendingUploads((prev) => {
          queueLength = prev.filter((u) => u.status === "pending").length;
          pending = prev.find((u) => u.status === "pending") || null;

          if (!pending) {
            resolve();
            return prev; // No pending items
          }

          // Mark as uploading
          const updated = prev.map((u) =>
            u.id === pending!.id ? { ...u, status: "uploading" as const } : u
          );
          resolve();
          return updated;
        });
      });

      // Exit loop if no pending items (queue length === 0)
      if (!pending || queueLength === 0) {
        console.log("[processUploadQueue] Queue empty, stopping");
        uploadQueueRef.current = false;
        break;
      }

      // TypeScript: pending is guaranteed to be non-null here after the check above
      const currentPending: PendingUpload = pending;

      console.log("[processUploadQueue] Processing upload", {
        fileName: currentPending.file.name,
        proofType: currentPending.proofType,
        uploadId: currentPending.id,
        queueLength,
      });

      try {
        // Upload the file
        const result = await uploadProofUseCase.execute(
          complianceId,
          currentPending.file,
          currentPending.proofType
        );

        // Remove from queued files ref
        const fileKey = `${currentPending.file.name}_${currentPending.file.size}_${currentPending.proofType}`;
        queuedFilesRef.current.delete(fileKey);

        // Mark as completed
        setPendingUploads((current) =>
          current.map((u) =>
            u.id === currentPending.id
              ? {
                  ...u,
                  status: "completed" as const,
                  proofId: result.id,
                  signed_url: result.signed_url,
                }
              : u
          )
        );

        // Add to proofs list
        setProofs((prev) => {
          const proofExists = prev.some((p) => p.id === result.id);
          if (proofExists) {
            return prev;
          }
          return [
            ...prev,
            {
              id: result.id,
              compliance_id: complianceId!,
              proof_type: result.proof_type,
              file_url: result.file_url,
              file_name: result.file_name,
              file_size: result.file_size,
              mime_type: result.mime_type,
              uploaded_by: result.uploaded_by,
              uploaded_at: result.uploaded_at,
              description: result.description,
              is_primary: result.is_primary,
              created_at: result.created_at,
              updated_at: result.updated_at,
              signed_url: result.signed_url,
            },
          ];
        });

        // Remove from queue after delay
        setTimeout(() => {
          setPendingUploads((current) =>
            current.filter((u) => u.id !== currentPending.id)
          );
        }, 1000);

        console.log("[processUploadQueue] Upload completed", {
          fileName: currentPending.file.name,
          proofId: result.id,
        });
      } catch (err) {
        // Remove from queued files ref on error
        const fileKey = `${currentPending.file.name}_${currentPending.file.size}_${currentPending.proofType}`;
        queuedFilesRef.current.delete(fileKey);

        // Mark as error
        setPendingUploads((current) =>
          current.map((u) =>
            u.id === currentPending.id
              ? {
                  ...u,
                  status: "error" as const,
                  error: err instanceof Error ? err.message : "Upload failed",
                }
              : u
          )
        );

        showError(
          err instanceof Error ? err.message : "Failed to upload proof"
        );

        console.log("[processUploadQueue] Upload failed", {
          fileName: currentPending.file.name,
          error: err instanceof Error ? err.message : "Unknown error",
        });
      }

      // Loop continues to check for next pending item
    }

    console.log("[processUploadQueue] Queue processing complete");
  }, [complianceId, showError]);

  // Helper function to queue upload when both file and proof type are available
  const queueUploadIfReady = useCallback(
    (file: File, type: string) => {
      // Create unique key for this file+type combination
      const fileKey = `${file.name}_${file.size}_${type}`;

      // Check if already queued (double-check with ref)
      if (queuedFilesRef.current.has(fileKey)) {
        return;
      }

      // Mark as queued immediately
      queuedFilesRef.current.add(fileKey);

      const tempId = `temp_${Date.now()}_${Math.random()}`;
      const newUpload: PendingUpload = {
        id: tempId,
        file: file,
        proofType: type,
        status: "pending",
      };

      setPendingUploads((prev) => {
        // Double-check: verify this exact file+type combo isn't already in queue
        const alreadyExists = prev.some(
          (u) =>
            u.file.name === file.name &&
            u.file.size === file.size &&
            u.proofType === type &&
            (u.status === "pending" || u.status === "uploading")
        );

        if (alreadyExists) {
          // Remove from ref since we're not adding it
          queuedFilesRef.current.delete(fileKey);
          return prev;
        }

        const wasEmpty = prev.length === 0; // Check if queue was empty
        const newQueue = [...prev, newUpload];

        // Only call processUploadQueue if queue was empty (now length === 1)
        // The while loop will handle processing all items
        if (wasEmpty && !uploadQueueRef.current) {
          setTimeout(() => {
            processUploadQueue();
          }, 0);
        }

        return newQueue;
      });
    },
    [processUploadQueue]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Prevent processing the same file multiple times
    const fileKey = `${file.name}_${file.size}_${file.lastModified}`;
    if (processingFileRef.current === fileKey) {
      return;
    }
    processingFileRef.current = fileKey;

    if (proofType) {
      // Both file and proof type are ready - queue immediately
      queueUploadIfReady(file, proofType);
      setSelectedFile(null);
      setProofType("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      // Reset processing ref after a delay
      setTimeout(() => {
        processingFileRef.current = null;
      }, 1000);
    } else {
      // Only file selected - store for later
      setSelectedFile(file);
      // Reset processing ref
      setTimeout(() => {
        processingFileRef.current = null;
      }, 500);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (proofType) {
        // Both file and proof type are ready - queue immediately
        queueUploadIfReady(file, proofType);
        setSelectedFile(null);
        setProofType("");
      } else {
        // Only file selected - store for later
        setSelectedFile(file);
      }
    }
  };

  const handleDeleteProof = async (proofId: number) => {
    if (!complianceId) return;

    try {
      await complianceRepository.deleteProof(proofId);
      showSuccess("Proof deleted successfully");
      // Remove from local proofs state (no need to refetch)
      setProofs((prev) => prev.filter((p) => p.id !== proofId));
      // Also remove from pending uploads if it exists there
      setPendingUploads((prev) => prev.filter((p) => p.proofId !== proofId));
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to delete proof");
    }
  };

  const handleSubmitForReview = async () => {
    if (!complianceId || !compliance) return;

    try {
      setSubmitting(true);
      await complianceRepository.submitForReview(complianceId);
      showSuccess("Compliance submitted for review successfully");

      // Update local compliance state only (no need to refetch list)
      const updatedCompliance = {
        ...compliance,
        status: "under_review" as const,
        submitted_for_review_at: new Date().toISOString(),
      };
      setCompliance(updatedCompliance);

      // Notify parent component to update status locally
      if (onStatusUpdate) {
        onStatusUpdate(complianceId, "under_review");
      }
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to submit for review"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMins = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMins < 1) return "Just now";
    if (diffInMins < 60)
      return `${diffInMins} ${diffInMins === 1 ? "min" : "mins"} ago`;
    if (diffInHours < 24)
      return `${diffInHours} ${diffInHours === 1 ? "hour" : "hours"} ago`;
    if (diffInDays < 7)
      return `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`;
    return formatDate(dateString);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "overdue":
        return <Badge variant="error">Overdue</Badge>;
      case "pending":
        return <Badge variant="warning">Pending</Badge>;
      case "under_review":
        return <Badge>Under Review</Badge>;
      case "completed":
      case "approved":
        return <Badge variant="success">Completed</Badge>;
      default:
        return <Badge variant="warning">Pending</Badge>;
    }
  };

  const getStatusIconVariant = (
    status: string
  ): "error" | "warning" | "neutral" | "success" => {
    switch (status) {
      case "overdue":
        return "error";
      case "pending":
        return "warning";
      case "under_review":
        return "neutral";
      case "completed":
      case "approved":
        return "success";
      default:
        return "neutral";
    }
  };

  const isOverdue = (dueDate: string, status: string): boolean => {
    if (status === "completed" || status === "approved") {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  if (!complianceId) {
    return null;
  }

  const typeDetails = compliance
    ? (compliance as any).compliance_type_details || {}
    : {};
  const isOverdueStatus = compliance
    ? isOverdue(compliance.due_date, compliance.status)
    : false;
  // Can submit if status is pending and has at least one proof (completed or uploaded)
  const canSubmit = compliance
    ? compliance.status === "pending" &&
      (proofs.length > 0 ||
        pendingUploads.some((u) => u.status === "completed"))
    : false;

  return (
    <div className="flex w-96 flex-none flex-col items-start self-stretch border-l border-solid border-neutral-border bg-default-background shadow-lg fixed right-0 top-0 h-screen overflow-y-auto z-[55]">
      <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
        <div className="flex w-full items-start justify-between">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Compliance Details
          </span>
          <IconButton
            variant="neutral-tertiary"
            size="small"
            icon={<FeatherX />}
            onClick={onClose}
          />
        </div>

        {loading ? (
          <div className="flex w-full items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-body font-body text-subtext-color">
                Loading...
              </p>
            </div>
          </div>
        ) : compliance ? (
          <>
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-4">
              <div className="flex w-full items-center gap-2">
                <IconWithBackground
                  variant={getStatusIconVariant(compliance.status)}
                  size="small"
                  icon={<FeatherAlertCircle />}
                />
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {compliance.compliance_form_name}
                </span>
              </div>
              <div className="flex w-full flex-col items-start gap-3">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Period
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {compliance.period}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Due Date
                  </span>
                  <span
                    className={`text-body-bold font-body-bold ${
                      isOverdueStatus ? "text-error-700" : "text-default-font"
                    }`}
                  >
                    {formatDate(compliance.due_date)}
                    {isOverdueStatus ? " (Overdue)" : ""}
                  </span>
                </div>
                {typeDetails.authority && (
                  <div className="flex w-full items-center justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Authority
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {typeDetails.authority}
                    </span>
                  </div>
                )}
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Status
                  </span>
                  {getStatusBadge(compliance.status)}
                </div>
              </div>
              {/* <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-200" />
              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-caption-bold font-caption-bold text-default-font">
                  Required documents:
                </span>
                <div className="flex items-center gap-2">
                  <FeatherCheck className="text-caption font-caption text-success-600" />
                  <span className="text-caption font-caption text-subtext-color">
                    {compliance.compliance_form_name} filing acknowledgement
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <FeatherCheck className="text-caption font-caption text-success-600" />
                  <span className="text-caption font-caption text-subtext-color">
                    Payment challan
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <FeatherSquare className="text-caption font-caption text-neutral-400" />
                  <span className="text-caption font-caption text-subtext-color">
                    Optional: Working sheet / calculation file
                  </span>
                </div>
              </div> */}
            </div>

            {compliance.status !== "completed" &&
              compliance.status !== "approved" &&
              compliance.status !== "sent_for_query" && (
                <div className="flex w-full flex-col items-start gap-4">
                  <span className="font-['Figtree'] text-[14px] font-[600] leading-[20px] text-default-font">
                    Upload Proofs *
                  </span>
                  <div className="flex w-full flex-col items-start gap-3">
                    <SubframeCore.DropdownMenu.Root
                      open={dropdownOpen}
                      onOpenChange={setDropdownOpen}
                    >
                      <SubframeCore.DropdownMenu.Trigger asChild={true}>
                        <Button
                          variant="neutral-secondary"
                          size="medium"
                          className="w-full justify-between"
                          iconRight={<FeatherChevronDown />}
                        >
                          {proofType
                            ? proofType
                                .split("_")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                )
                                .join(" ")
                            : "Select proof type"}
                        </Button>
                      </SubframeCore.DropdownMenu.Trigger>
                      <SubframeCore.DropdownMenu.Portal>
                        <SubframeCore.DropdownMenu.Content
                          side="bottom"
                          align="start"
                          sideOffset={4}
                          asChild={true}
                        >
                          <DropdownMenu className="dropdown-menu-content">
                            {proofTypeOptions.map((option) => (
                              <DropdownMenu.DropdownItem
                                key={option}
                                onClick={() => {
                                  if (selectedFile) {
                                    // Both file and proof type are ready - queue immediately
                                    queueUploadIfReady(selectedFile, option);
                                    setSelectedFile(null);
                                    setProofType("");
                                    setDropdownOpen(false);
                                    if (fileInputRef.current) {
                                      fileInputRef.current.value = "";
                                    }
                                  } else {
                                    // Only proof type selected - store for later
                                    setProofType(option);
                                    setDropdownOpen(false);
                                  }
                                }}
                              >
                                {option
                                  .split("_")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1)
                                  )
                                  .join(" ")}
                              </DropdownMenu.DropdownItem>
                            ))}
                          </DropdownMenu>
                        </SubframeCore.DropdownMenu.Content>
                      </SubframeCore.DropdownMenu.Portal>
                    </SubframeCore.DropdownMenu.Root>
                    <div
                      className={`flex h-32 w-full flex-none flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed ${
                        isDragging
                          ? "border-brand-500 bg-brand-50"
                          : "border-neutral-300 bg-neutral-50"
                      } px-4 py-4 cursor-pointer`}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onClick={handleFileSelect}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                      />
                      <FeatherUpload className="text-heading-2 font-heading-2 text-neutral-400" />
                      {selectedFile ? (
                        <>
                          <span className="text-body font-body text-default-font">
                            {selectedFile.name}
                          </span>
                          <span className="text-caption font-caption text-subtext-color">
                            {(selectedFile.size / 1024).toFixed(2)} KB
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-body font-body text-neutral-500">
                            Drag and drop files here
                          </span>
                          <span className="text-caption font-caption text-neutral-400">
                            or click to browse
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {compliance.status === "sent_for_query" && (
              <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-4">
                <div className="flex items-center gap-2">
                  <FeatherMessageCircle className="text-body font-body text-neutral-600" />
                  <span className="text-body font-body text-default-font">
                    Query has been raised for this compliance item
                  </span>
                </div>
                <span className="text-caption font-caption text-subtext-color">
                  Please wait for the client to respond before proceeding with
                  upload or review.
                </span>
              </div>
            )}

            <div className="flex w-full flex-col items-start gap-3">
              <span className="text-body-bold font-body-bold text-default-font">
                Uploaded Files
              </span>
              {proofs.length === 0 && pendingUploads.length === 0 ? (
                <div className="flex w-full items-center justify-center p-4 text-body font-body text-subtext-color">
                  No files uploaded yet
                </div>
              ) : (
                <div className="flex w-full flex-col items-start gap-2">
                  {/* Show pending uploads (dummy tiles) */}
                  {pendingUploads.map((pending) => (
                    <div
                      key={pending.id}
                      className="flex w-full items-center gap-3 rounded-md border border-solid border-neutral-border bg-white px-3 py-2"
                    >
                      <FeatherFile className="text-body font-body text-neutral-500" />
                      <div className="flex grow shrink-0 basis-0 flex-col items-start">
                        <span className="text-caption-bold font-caption-bold text-default-font">
                          {pending.file.name}
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          {pending.status === "pending" && "Queued..."}
                          {pending.status === "uploading" && "Uploading..."}
                          {pending.status === "error" && pending.error}
                          {pending.status === "completed" && "Uploaded"}
                        </span>
                      </div>
                      {pending.status === "uploading" && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      )}
                      {pending.status === "error" && (
                        <IconButton
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherTrash2 />}
                          onClick={() => {
                            setPendingUploads((prev) =>
                              prev.filter((u) => u.id !== pending.id)
                            );
                          }}
                        />
                      )}
                      {pending.status === "completed" && pending.proofId && (
                        <>
                          <IconButton
                            variant="neutral-tertiary"
                            size="small"
                            icon={<FeatherDownload />}
                            onClick={() => {
                              // Use stored signed_url directly from upload response
                              if (pending.signed_url) {
                                window.open(pending.signed_url, "_blank");
                              } else {
                                showError(
                                  "Document not available yet. Please try again in a moment."
                                );
                              }
                            }}
                          />
                          {compliance &&
                            compliance.status !== "completed" &&
                            compliance.status !== "approved" &&
                            compliance.status !== "under_review" && (
                              <IconButton
                                variant="neutral-tertiary"
                                size="small"
                                icon={<FeatherTrash2 />}
                                onClick={() =>
                                  handleDeleteProof(pending.proofId!)
                                }
                              />
                            )}
                        </>
                      )}
                    </div>
                  ))}
                  {/* Show completed proofs */}
                  {proofs.map((proof) => {
                    // Skip if this proof is in pending uploads (already shown above)
                    const isPending = pendingUploads.some(
                      (p) => p.proofId === proof.id
                    );
                    if (isPending) return null;

                    return (
                      <div
                        key={proof.id}
                        className="flex w-full items-center gap-3 rounded-md border border-solid border-neutral-border bg-white px-3 py-2"
                      >
                        <FeatherFile className="text-body font-body text-neutral-500" />
                        <div className="flex grow shrink-0 basis-0 flex-col items-start">
                          <span className="text-caption-bold font-caption-bold text-default-font">
                            {proof.file_name || `Proof ${proof.id}`}
                          </span>
                          <span className="text-caption font-caption text-subtext-color">
                            {proof.uploaded_at
                              ? formatRelativeTime(proof.uploaded_at)
                              : "Uploaded"}
                          </span>
                        </div>
                        {proof.signed_url && (
                          <IconButton
                            variant="neutral-tertiary"
                            size="small"
                            icon={<FeatherDownload />}
                            onClick={() => {
                              window.open(proof.signed_url, "_blank");
                            }}
                          />
                        )}
                        {compliance &&
                          compliance.status !== "completed" &&
                          compliance.status !== "approved" &&
                          compliance.status !== "under_review" && (
                            <IconButton
                              variant="neutral-tertiary"
                              size="small"
                              icon={<FeatherTrash2 />}
                              onClick={() => handleDeleteProof(proof.id)}
                            />
                          )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {canSubmit && (
              <Button
                className="h-10 w-full flex-none"
                icon={<FeatherSend />}
                onClick={handleSubmitForReview}
                disabled={
                  submitting ||
                  pendingUploads.some(
                    (u) => u.status === "uploading" || u.status === "pending"
                  )
                }
              >
                {submitting ? "Submitting..." : "Submit for Review"}
              </Button>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};
